import React from 'react'
import { useApp } from '../../context/AppContext'
import { Icon, useT } from '../../shared'
import { tierShort } from '../../lib/tier'
import { useDropdown } from './useDropdown'

// Baris kecil di bawah nama kelas: "Bandung · 13–15 Okt 2026". Banyak nama kelas
// sudah memuat lokasinya; baris ini dilewati supaya tidak tertulis dua kali.
function classMeta(k, lang) {
  if (!k) return ''
  const name = (k.name || '').toLowerCase()
  if (k.location && name.includes(k.location.trim().toLowerCase())) return ''
  const fmt = new Intl.DateTimeFormat(lang === 'id' ? 'id-ID' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  const start = k.start_date ? new Date(k.start_date) : null
  const end = k.end_date ? new Date(k.end_date) : null
  const dates = start && end && start <= end ? fmt.formatRange(start, end) : start ? fmt.format(start) : ''
  return [k.location, dates].filter(Boolean).join(' · ')
}

// Satu kartu konteks untuk "sedang bekerja di mana": program dan (untuk staf)
// kelasnya. Menggantikan dua kotak pemilih terpisah yang menumpuk di atas menu
// dan memotong nama kelas.
//
// Aturan yang dipertahankan dari pemilih lama:
//  - tidak tampil untuk staf platform: GET /programs mengembalikan program
//    SEMUA lembaga untuk mereka, dan peran itu tidak punya konteks program;
//  - bagian kelas hanya untuk admin/trainer dan hanya bila programnya punya
//    kelas (pemilih kosong hanya membingungkan);
//  - "Semua kelas" tetap ada: laporan lintas angkatan dan layar lama yang
//    belum berbasis kelas masih membutuhkannya.
export default function ContextCard() {
  const {
    role, lang, programs, currentProgram, currentProgramId, setCurrentProgramId,
    classes, currentClass, currentClassId, setCurrentClassId,
    sidebarCollapsed: rail, toggleSidebar,
  } = useApp()
  const t = useT()
  const dd = useDropdown()
  const wrapRef = React.useRef(null)
  const triggerRef = React.useRef(null)

  const showProgram = role !== 'platform' && programs.length > 0
  const showClass = showProgram && (role === 'admin' || role === 'trainer') && classes.length > 0

  // Tutup dengan klik di luar atau Esc. Sebelumnya menutup sendiri begitu kursor
  // keluar, yang membuat panel hilang saat kursor sekadar melenceng sedikit —
  // dan tidak berfungsi sama sekali di layar sentuh.
  React.useEffect(() => {
    if (!dd.open) return undefined
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) dd.hide()
    }
    const onKey = (e) => {
      if (e.key === 'Escape') { dd.hide(); triggerRef.current?.focus() }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dd.open])

  if (!showProgram) return null

  // tierShort menghormati sebutan tingkat milik lembaga ("Dasar") dan program
  // tanpa tingkat; menulis `L{level}` di sini mengabaikan keduanya.
  const progTier = tierShort(currentProgram)
  const progCode = currentProgram?.code?.toUpperCase() || t.ctxSelectProgram
  const progValue = `${progCode}${progTier ? ` · ${progTier}` : ''}`
  const progChip = currentProgram?.code ? progValue : ''
  const className = currentClass?.name || t.ctxAllClasses
  const classSub = classMeta(currentClass, lang)
  const dotColor = currentProgram?.color_theme || 'var(--program-color)'

  const pickProgram = (id) => {
    setCurrentProgramId(id)
    // Daftar kelas baru diambil setelah program berganti; menutup panel di sini
    // mencegah memilih kelas dari daftar program sebelumnya yang belum diganti.
    dd.hide()
  }
  const pickClass = (id) => { setCurrentClassId(id); dd.hide() }

  return (
    <>
      {/* Pada rail ikon kartu disembunyikan; titik ini menggantikannya dan
          membuka kembali sidebar supaya program/kelas bisa dipilih. */}
      <button
        type="button"
        className="rail-context"
        onClick={toggleSidebar}
        title={`${t.ctxProgram}: ${progValue}${showClass ? ` · ${t.ctxClass}: ${className}` : ''}`}
        aria-label={t.navExpand}
      >
        <span className="program-dot" style={{ background: dotColor }} />
      </button>

      <div className="context-block" ref={wrapRef}>
        <button
          type="button"
          ref={triggerRef}
          className="context-card"
          onClick={dd.toggle}
          aria-haspopup="true"
          aria-expanded={dd.open}
          aria-label={t.ctxChange}
        >
          <span className="program-dot" style={{ background: dotColor }} />
          <span className="context-card-rows">
            <span className="context-card-row">
              <span className="context-card-head">
                <span className="context-card-label">{t.ctxProgram}</span>
                {progChip && <span className="context-card-chip">{progChip}</span>}
              </span>
              <span className="context-card-value" title={currentProgram?.name}>{currentProgram?.name || progValue}</span>
            </span>
            {showClass && (
              <span className="context-card-row">
                <span className="context-card-label">{t.ctxClass}</span>
                <span className="context-card-value" title={className}>{className}</span>
                {classSub && <span className="context-card-sub">{classSub}</span>}
              </span>
            )}
          </span>
          <Icon
            name="chevron_down"
            size={14}
            className="program-switcher-chevron"
            style={{ color: 'var(--text-3)', flexShrink: 0, transform: dd.open ? 'rotate(180deg)' : 'none' }}
          />
        </button>

        {dd.mounted && (
          <div className={'program-dropdown context-panel' + (dd.open ? '' : ' closing')}>
            <div className="context-panel-title">{t.ctxProgram}</div>
            {programs.map((p) => {
              const selected = p.id === currentProgramId
              return (
                <button
                  key={p.id}
                  type="button"
                  className={'program-dropdown-item' + (selected ? ' active' : '')}
                  aria-current={selected ? 'true' : undefined}
                  onClick={() => pickProgram(p.id)}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color_theme || 'var(--program-color)', flexShrink: 0 }} />
                  <div>
                    <div className="program-dropdown-item-name">
                      {p.code?.toUpperCase()}{tierShort(p) ? ` · ${tierShort(p)}` : ''}
                      {/* Hanya admin lembaga yang masih melihat program nonaktif. */}
                      {p.is_active === false && (
                        <span className="tiny muted" style={{ marginLeft: 6, fontWeight: 400 }}>
                          {lang === 'id' ? 'nonaktif' : 'inactive'}
                        </span>
                      )}
                    </div>
                    <div className="program-dropdown-item-sub">{p.name}</div>
                  </div>
                  {selected && <Icon name="check" size={14} style={{ color: 'var(--program-color)', marginLeft: 'auto' }} />}
                </button>
              )
            })}

            {showClass && (
              <>
                <div className="context-panel-sep" />
                <div className="context-panel-title">{t.ctxClass}</div>
                <button
                  type="button"
                  className={'program-dropdown-item' + (!currentClassId ? ' active' : '')}
                  aria-current={!currentClassId ? 'true' : undefined}
                  onClick={() => pickClass(null)}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--text-3)', flexShrink: 0 }} />
                  <div>
                    <div className="program-dropdown-item-name">{t.ctxAllClasses}</div>
                    <div className="program-dropdown-item-sub">{t.ctxAllClassesSub}</div>
                  </div>
                  {!currentClassId && <Icon name="check" size={14} style={{ color: 'var(--program-color)', marginLeft: 'auto' }} />}
                </button>
                {classes.map((k) => {
                  const selected = k.id === currentClassId
                  return (
                    <button
                      key={k.id}
                      type="button"
                      className={'program-dropdown-item' + (selected ? ' active' : '')}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => pickClass(k.id)}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: k.mode === 'INHOUSE' ? 'var(--program-color)' : 'var(--text-3)', flexShrink: 0 }} />
                      <div>
                        <div className="program-dropdown-item-name">{k.name}</div>
                        <div className="program-dropdown-item-sub">
                          {k.mode === 'INHOUSE' ? (k.client_company || 'Inhouse') : t.ctxPublic}
                          {typeof k._count?.enrollments === 'number' ? ` · ${k._count.enrollments} ${t.ctxParticipants}` : ''}
                        </div>
                      </div>
                      {selected && <Icon name="check" size={14} style={{ color: 'var(--program-color)', marginLeft: 'auto' }} />}
                    </button>
                  )
                })}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
